import { useQuery } from '@apollo/client/react';
import { CheckCircleOutline, Close } from '@mui/icons-material';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useRecordContext } from 'react-admin';

import {
  AdminGetUserFeaturesDocument,
  AdminListFeaturesDocument,
  AdminUserDetailsFragment,
} from '../../../../graphql/types';
import { getGraphQLErrorDetails } from '../../../../utils/getGraphQLErrorDetails';
import { ResourceLink } from '../../../reusable/ResourceLink';

// The catalog is a handful of rows; a key beyond this page still reads on its
// own, it just loses the name and the link.
const CATALOG_PAGE_SIZE = 100;

/**
 * The engine's reason leads with the state it decided ("enabled: client
 * grant") — that half is already the State column, so keep only the trace.
 */
export const describeReason = (reason?: string | null): string => {
  const trace = reason?.replace(/^(enabled|disabled):\s*/, '').trim();
  return trace || '—';
};

export const MemberEffectiveFeatures: React.FC = () => {
  const record = useRecordContext<AdminUserDetailsFragment>();
  const { data, error, loading } = useQuery(AdminGetUserFeaturesDocument, {
    variables: {
      userId: record!.id, // see skip
    },
    skip: !record?.id,
    // Creating or editing a grant leaves the tab and comes back, so the default
    // cache-first would replay a resolution the edit just invalidated.
    fetchPolicy: 'cache-and-network',
  });

  // A resolved state carries only the feature's key, so pair it with the
  // catalog for the name admins recognize and a link to the feature itself.
  const { data: catalog } = useQuery(AdminListFeaturesDocument, {
    variables: {
      filter: { isArchived: false },
      input: { first: CATALOG_PAGE_SIZE },
    },
    fetchPolicy: 'cache-first',
  });

  const features = data?.user?.features ?? [];
  const catalogByKey = new Map(
    (catalog?.data.nodes ?? []).map((feature) => [feature.key, feature]),
  );

  const { errorRequestId, errorPath } = getGraphQLErrorDetails(error);

  return (
    <>
      <Typography variant="h6" fontSize={16}>
        Effective features
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        What the engine actually grants this member, after walking scope
        precedence (user, location, studio, country, client) and falling back to
        the feature default. Read-only — edit the grant that decided it.
      </Typography>

      {/* Keep the table up during a revalidation rather than flashing back. */}
      {loading && features.length === 0 && (
        <Typography>Loading effective features…</Typography>
      )}

      {error && (
        <>
          <Typography color="error">
            An error occurred while resolving this member&apos;s features.
          </Typography>
          {errorPath && (
            <Typography color="error" fontSize={12}>
              Path: {errorPath}
            </Typography>
          )}
          {errorRequestId && (
            <Typography color="error" fontSize={12}>
              Request Id: {errorRequestId}
            </Typography>
          )}
        </>
      )}

      {!loading && !error && features.length === 0 && (
        <Typography>No active features in the catalog.</Typography>
      )}

      {features.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Feature</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {features.map((feature) => {
                const catalogEntry = catalogByKey.get(feature.key);
                return (
                  <TableRow key={feature.key}>
                    <TableCell>
                      {catalogEntry ? (
                        <>
                          <ResourceLink name="Feature" id={catalogEntry.id}>
                            {catalogEntry.name}
                          </ResourceLink>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {feature.key}
                          </Typography>
                        </>
                      ) : (
                        feature.key
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {feature.enabled ? (
                          <CheckCircleOutline
                            color="success"
                            fontSize="small"
                          />
                        ) : (
                          <Close color="error" fontSize="small" />
                        )}
                        {feature.enabled ? 'Enabled' : 'Disabled'}
                      </Box>
                    </TableCell>
                    <TableCell>{describeReason(feature.reason)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
